import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getServiceById,
  createService,
  updateService,
  uploadServiceImage,
  getServiceStaffAssignments,
  createServiceStaffAssignment,
  updateServiceStaffAssignment,
  deleteServiceStaffAssignment,
  getUsers,
  getTaxGroups,
  getCategories,
  getAssetUrl,
} from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import SelectBox from "../components/UI/SelectBox";
import ToggleSwitch from "../components/UI/ToggleSwitch";
import { notify } from "../utils/notify";
import PageHeader from "../components/UI/PageHeader";
import EntityTranslationsButton from "../components/UI/EntityTranslationsButton";
import { useTranslation } from "../context/I18nContext";

// EMPTY_ASSIGNMENT rows have no `id` yet — that's exactly how handleSubmit
// tells a brand-new assignment (needs createServiceStaffAssignment) apart
// from one loaded from the server (needs updateServiceStaffAssignment) —
// see models.ServiceStaffAssignment / service_handler.go.
const EMPTY_ASSIGNMENT = { id: null, user_id: "", commission_type: "percentage", commission_value: "" };

export default function AddEditService() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("services");

  const [users, setUsers] = useState([]);
  const [taxGroups, setTaxGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    name: "",
    description: "",
    duration_minutes: "30",
    price: "",
    category_id: "",
    tax_group_id: "",
    allow_multiple_staff: false,
    is_active: true,
    // loyalty_points is a fixed bonus number of loyalty points this
    // service grants PER SALE, additive on top of whatever the order
    // already earns from spend-based points — see Service.LoyaltyPoints
    // and the "Earn on Product" toggle on the Loyalty settings screen
    // (the same toggle a product's bonus points use). 0 (blank) means
    // this service grants no bonus points of its own.
    loyalty_points: "",
  });
  // assignments holds both existing (id set) and newly-added (id null)
  // rows; removedIds tracks existing rows the admin deleted from the form
  // so handleSubmit can call deleteServiceStaffAssignment for each on save
  // — nothing is actually deleted from the server until Save is pressed.
  const [assignments, setAssignments] = useState([]);
  const [removedIds, setRemovedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // imagePreview/imageFile mirror AddEditProduct.js exactly — see that
  // page's own comment for why imageFile (not imagePreview) is what tells
  // handleSubmit to actually call uploadServiceImage afterwards.
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    getUsers(token, { limit: 1000 }).then((res) => setUsers(res?.data || res || [])).catch(() => notify.error(t("notify_load_users_failed", "Failed to load staff")));
    getTaxGroups(token).then(setTaxGroups).catch(() => notify.error(t("notify_load_tax_groups_failed", "Failed to load tax groups")));
    getCategories(token).then(setCategories).catch(() => notify.error(t("notify_load_categories_failed", "Failed to load categories")));

    if (id) {
      getServiceById(token, id).then((svc) => {
        setForm({
          name: svc.name || "",
          description: svc.description || "",
          duration_minutes: String(svc.duration_minutes ?? "30"),
          price: String(svc.price ?? ""),
          category_id: svc.category_id ? String(svc.category_id) : "",
          tax_group_id: svc.tax_group_id ? String(svc.tax_group_id) : "",
          allow_multiple_staff: !!svc.allow_multiple_staff,
          is_active: svc.is_active !== undefined ? svc.is_active : true,
          loyalty_points: svc.loyalty_points ?? "",
        });
        if (svc.image_url) setImagePreview(svc.image_url);
      }).catch(() => notify.error(t("notify_load_failed", "Failed to load service")));

      getServiceStaffAssignments(token, id).then((rows) => {
        setAssignments((rows || []).map((r) => ({
          id: r.id,
          user_id: String(r.user_id),
          commission_type: r.commission_type,
          commission_value: String(r.commission_value ?? ""),
        })));
      }).catch(() => {});
    }
  }, [id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const userOptions = users.map((u) => ({ value: String(u.id), label: u.job_title ? `${u.username} — ${u.job_title}` : u.username }));
  const categoryOptions = categories.map((c) => ({ value: String(c.id), label: c.name }));
  const taxGroupOptions = [{ value: "", label: t("tax_group_none_option", "No tax (exempt)") }, ...taxGroups.map((g) => ({ value: String(g.id), label: g.name }))];
  const commissionTypeOptions = [
    { value: "percentage", label: t("commission_type_percentage", "Percentage") },
    { value: "fixed", label: t("commission_type_fixed", "Fixed amount") },
  ];

  const setField = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const updateAssignment = (idx, field, value) => {
    setAssignments((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const addAssignment = () => setAssignments((prev) => [...prev, { ...EMPTY_ASSIGNMENT }]);

  const removeAssignment = (idx) => {
    setAssignments((prev) => {
      const row = prev[idx];
      if (row.id) setRemovedIds((ids) => [...ids, row.id]);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleSubmit = async () => {
    setErrors({});
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = t("validation_name_required", "Service name is required");
    if (!form.duration_minutes || Number(form.duration_minutes) <= 0) newErrors.duration_minutes = t("validation_duration_required", "Duration must be greater than zero");
    if (form.price === "" || Number(form.price) < 0) newErrors.price = t("validation_price_required", "Enter a valid price");
    if (!form.category_id) newErrors.category_id = t("validation_category_required", "Category is required");
    if (form.loyalty_points !== "" && Number(form.loyalty_points) < 0) newErrors.loyalty_points = t("validation_loyalty_negative", "Loyalty points cannot be negative");
    assignments.forEach((a, i) => {
      if (!a.user_id) newErrors[`assignment_${i}`] = t("validation_assignment_staff_required", "Pick a staff member");
      else if (a.commission_value === "" || Number(a.commission_value) < 0) newErrors[`assignment_${i}`] = t("validation_assignment_commission_required", "Enter a valid commission value");
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      notify.error(tCommon("validation_fill_required_fields", "Please fill in the required fields correctly"));
      return;
    }

    setLoading(true);
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      duration_minutes: Number(form.duration_minutes),
      price: Number(form.price),
      category_id: Number(form.category_id),
      tax_group_id: form.tax_group_id ? Number(form.tax_group_id) : 0,
      allow_multiple_staff: form.allow_multiple_staff,
      is_active: form.is_active,
      loyalty_points: form.loyalty_points === "" ? 0 : Number(form.loyalty_points),
    };

    try {
      let serviceId = id;
      if (id) {
        await updateService(token, id, payload);
      } else {
        const created = await createService(token, payload);
        serviceId = created.id;
      }

      // The picture is its own upload, separate from the JSON payload
      // above (see uploadServiceImage/UploadServiceImageHandler) — only
      // fired when the user actually picked a new file this session, and
      // only once serviceId is known (freshly created, or the one being
      // edited). Mirrors AddEditProduct.js's identical image-upload step.
      if (imageFile && serviceId) {
        try {
          await uploadServiceImage(token, serviceId, imageFile);
        } catch (imgErr) {
          notify.error(imgErr.data?.error || imgErr.message || t("notify_image_upload_failed", "Service saved, but the image failed to upload"));
        }
      }

      // Staff assignments are their own resource (service_staff_assignments
      // table), not part of the Service payload above — see
      // service_handler.go. Applying the form's current rows here means
      // nothing on the server changes until Save is actually pressed, even
      // though each row edit only touches local state until then.
      for (const removedId of removedIds) {
        await deleteServiceStaffAssignment(token, removedId);
      }
      for (const a of assignments) {
        const rowPayload = { user_id: Number(a.user_id), commission_type: a.commission_type, commission_value: Number(a.commission_value) };
        if (a.id) {
          await updateServiceStaffAssignment(token, a.id, rowPayload);
        } else {
          await createServiceStaffAssignment(token, serviceId, rowPayload);
        }
      }

      notify.success(id ? t("notify_updated", "Service updated successfully") : t("notify_created", "Service created successfully"));
      navigate("/services");
    } catch (err) {
      notify.error(err.data?.error || err.message || tCommon("error_generic", "Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto">
      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button onClick={() => navigate("/services")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">{id ? t("edit_title", "Edit Service") : t("add_title", "Add Service")}</h2>
          <p className="text-gray-400 text-sm italic font-medium">{t("form_subtitle", "A sellable, non-stock offering — set who can perform it and their commission below")}</p>
        </div>
      </PageHeader>

      <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <div className="space-y-6">
          <div className="flex flex-col items-center pb-2">
            <div className="relative mb-3">
              <div className="w-32 h-32 rounded-[2rem] overflow-hidden ring-8 ring-gray-50 bg-gray-100 flex items-center justify-center transition-all hover:ring-[var(--primary-color)]/10">
                {imagePreview ? (
                  <img
                    src={getAssetUrl(imagePreview)}
                    alt="Service"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <ICONS.Services className="w-12 h-12 text-gray-300" />
                )}
              </div>
              <label className="absolute bottom-1 right-1 bg-[var(--primary-color)] text-white p-2.5 rounded-2xl shadow-xl cursor-pointer hover:scale-110 transition-transform">
                <ICONS.Plus className="w-4 h-4" />
                <input type="file" className="hidden" onChange={handleImageChange} accept="image/*" />
              </label>
            </div>
            <p className="text-[10px] text-gray-400 font-medium">{t("image_hint", "PNG, JPG, GIF or WEBP. Max 5MB.")}</p>
            {imagePreview && (
              <button
                type="button"
                onClick={() => { setImageFile(null); setImagePreview(null); }}
                className="mt-2 text-[10px] font-black uppercase text-red-400 hover:text-red-600 transition-colors"
              >
                {t("btn_remove_photo", "Remove Photo")}
              </button>
            )}
          </div>

          <div className="flex items-end gap-3">
            <div className="flex-grow">
              <FloatingInput label={t("field_name_label", "Service Name")} value={form.name} onChange={setField("name")} error={errors.name} icon={ICONS.Services} />
            </div>
            <EntityTranslationsButton entityType="service" entityId={id} defaultName={form.name} />
          </div>

          <FloatingInput label={t("field_description_label", "Description (optional)")} value={form.description} onChange={setField("description")} />

          <div>
            <SelectBox
              label={t("col_category", "Category")}
              options={categoryOptions}
              value={form.category_id}
              onChange={(val) => {
                setForm((prev) => ({ ...prev, category_id: val }));
                if (errors.category_id) setErrors((prev) => ({ ...prev, category_id: undefined }));
              }}
            />
            {errors.category_id && <p className="text-[10px] text-red-500 font-bold mt-1 ms-2 uppercase tracking-tight">{errors.category_id}</p>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FloatingInput label={t("field_duration_label", "Duration (minutes)")} type="number" value={form.duration_minutes} onChange={setField("duration_minutes")} error={errors.duration_minutes} />
            <FloatingInput label={t("field_price_label", "Price")} type="number" value={form.price} onChange={setField("price")} error={errors.price} />
          </div>

          <SelectBox
            label={t("field_tax_group_label", "Tax Group")}
            options={taxGroupOptions}
            value={form.tax_group_id}
            onChange={(val) => setForm((prev) => ({ ...prev, tax_group_id: val }))}
          />

          <ToggleSwitch
            label={t("field_allow_multiple_staff_label", "Allow more than one staff member on the same sale")}
            checked={form.allow_multiple_staff}
            onChange={(val) => setForm((prev) => ({ ...prev, allow_multiple_staff: val }))}
          />

          <ToggleSwitch
            label={tCommon("status_active", "Active")}
            checked={form.is_active}
            onChange={(val) => setForm((prev) => ({ ...prev, is_active: val }))}
          />

          <p className="text-[11px] text-gray-400">
            {t("loyalty_points_hint", "A fixed number of bonus points this service earns per sale, additive on top of spend-based points. 0 means this service grants no bonus points of its own.")}
          </p>
          <FloatingInput
            label={t("field_loyalty_points_label", "Loyalty Points")}
            type="number"
            min="0"
            value={form.loyalty_points}
            onChange={setField("loyalty_points")}
            error={errors.loyalty_points}
            icon={ICONS.BundleOffers}
          />

          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold uppercase text-gray-500">{t("staff_section_title", "Staff & Commission")}</h3>
              <Button variant="outline" size="sm" icon={ICONS.Plus} onClick={addAssignment}>{t("btn_add_staff", "Add Staff Member")}</Button>
            </div>
            <p className="text-gray-400 text-xs mb-3">{t("staff_section_hint", "Each staff member here can perform this service, at their own commission rate — percentage or a fixed amount, whichever fits")}</p>

            {assignments.length === 0 && (
              <p className="text-gray-400 text-sm italic py-4 text-center bg-gray-50/50 rounded-2xl border border-gray-100">{t("staff_section_empty", "No staff assigned yet")}</p>
            )}

            <div className="space-y-3">
              {assignments.map((a, idx) => (
                <div key={a.id ?? `new-${idx}`} className="grid grid-cols-1 md:grid-cols-[2fr_1.3fr_1fr_auto] gap-2 items-start p-3 rounded-2xl bg-gray-50/50 border border-gray-100">
                  <SelectBox
                    label={t("field_staff_label", "Staff Member")}
                    options={userOptions}
                    value={a.user_id}
                    onChange={(val) => updateAssignment(idx, "user_id", val)}
                    error={errors[`assignment_${idx}`]}
                  />
                  <SelectBox
                    label={t("field_commission_type_label", "Commission Type")}
                    options={commissionTypeOptions}
                    value={a.commission_type}
                    onChange={(val) => updateAssignment(idx, "commission_type", val)}
                  />
                  <FloatingInput
                    label={a.commission_type === "fixed" ? t("field_commission_value_fixed_label", "Amount") : t("field_commission_value_percentage_label", "Percent")}
                    type="number"
                    value={a.commission_value}
                    onChange={(e) => updateAssignment(idx, "commission_value", e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => removeAssignment(idx)}
                    className="h-14 w-14 flex items-center justify-center rounded-xl text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                  >
                    <ICONS.DeleteUser className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-4 pt-6 border-t border-gray-50">
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
              className="flex-grow shadow-lg shadow-blue-500/10"
              icon={id ? ICONS.Save : ICONS.Plus}
            >
              {id ? t("btn_update_service", "Update Service") : t("btn_create_service", "Create Service")}
            </Button>
            <Button variant="secondary" onClick={() => navigate("/services")}>{tCommon("btn_cancel", "Cancel")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
